import { Field } from '../ui'

/**
 * "Woke up at" — a clock reading on a 24-hour clock (Sprint 1.9.11).
 *
 * ── WHY `type="time"` AND NOT A STEPPER OR A SELECT ─────────────────────────
 *
 * Every other journal field is a count or a choice, and the two controls this
 * form already owns — `NumberStepper` and `ChoiceChips` — fit those. A wake time
 * is neither: it is one of 1,440 values that a person knows exactly and reads
 * off a clock. A stepper would be 1,440 taps; a select would be a scroll through
 * a list nobody wants to search. The native control is the only one that offers
 * the phone's own time keyboard, and it emits exactly the `"HH:MM"` the contract
 * takes.
 *
 * ── AND WHY IT IS STILL VALIDATED ───────────────────────────────────────────
 *
 * `type="time"` degrades to a plain text box where it is unsupported, and a
 * paste reaches the field in any browser. `readClockTime` is the same rule the
 * backend's pattern states, and `draftErrors` surfaces it — so the degraded case
 * is a named error rather than a 400 the person reads as a server fault.
 *
 * The value is never repaired, only refused: `"7:00"` is not padded to `"07:00"`
 * for the reason `numericText` is `String(n)`. A client that rewrites an answer
 * is a client that can rewrite the wrong one.
 */
export function ClockField({
  value,
  onChange,
  error,
}: {
  value: string
  onChange: (next: string) => void
  error: string | null
}) {
  return (
    <Field
      label="Woke up at"
      type="time"
      inputMode="numeric"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      error={error}
      hint="Lets BrainSpot know how long you had been awake when you recorded."
    />
  )
}
